import {
  Component,
  Input,
  Output,
  EventEmitter,
  forwardRef,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { UmssSelectComponent, SelectOption } from '../select/select.component';
import { UmssButtonGroupComponent } from '../button-group/button-group.component';
import { UmssToggleButtonComponent } from '../toggle-button/toggle-button.component';
import {
  ContractorDocumentType,
  DocumentListCategoryEnum,
  DOCUMENT_LIST_CATEGORY_RECORD,
  getGroupedDocumentTypeOptions
} from '@umss/shared/data-access';

export type DocumentTypeFilter = 'all' | DocumentListCategoryEnum;
export type DocumentTypeSelectValue =
  | ContractorDocumentType
  | ContractorDocumentType[]
  | null;

@Component({
  selector: 'umss-contractor-document-type-select',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    UmssSelectComponent,
    UmssButtonGroupComponent,
    UmssToggleButtonComponent
  ],
  template: `
    @if (displayMode === 'select') {
      <umss-select
        [options]="options"
        [placeholder]="placeholder"
        [label]="showLabel ? label : undefined"
        [helperText]="helperText"
        [errorMessage]="errorMessage"
        [size]="size"
        [disabled]="disabled"
        [required]="required"
        [clearable]="clearable"
        [filterStyle]="filterStyle"
        [searchable]="true"
        [error]="error"
        [loading]="{ selector: loading }"
        [multiple]="multiple"
        [hideSelectedOptions]="hideSelectedOptions"
        [(value)]="value"
        (selectionChange)="onSelectionChange($event)"
        (searchChange)="onSearch($event)"
        (onFocus)="onFocus()"
        (onBlur)="onBlur()"
      />
    } @else {
      <umss-button-group
        [formControl]="internalControl"
        [label]="showLabel ? label : undefined"
        [size]="size"
        [variant]="variant"
        [disabled]="disabled"
        [required]="required"
        [allowWrap]="true"
        [multiple]="multiple"
        [clearable]="true"
        [maxPerRow]="maxPerRow"
      >
        @for (option of _buttonGroupOptions; track option.value) {
          <umss-toggle-button [label]="option.label" [value]="option.value" />
        }
      </umss-button-group>
    }
  `,
  styles: `
    :host {
      display: contents;
    }

    ::ng-deep .vertical-field {
      flex: 1;
      min-width: 0;
    }
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UmssContractorDocumentTypeSelectComponent),
      multi: true
    }
  ]
})
export class UmssContractorDocumentTypeSelectComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy
{
  @Input() displayMode: 'select' | 'button-group' = 'select';
  @Input() showLabel: boolean = false;
  @Input() label?: string = 'Document Type';
  @Input() placeholder: string = 'Select a document type';
  @Input() helperText?: string;
  @Input() errorMessage?: string;
  @Input() size: 'Small' | 'Regular' | 'Large' = 'Regular';
  @Input() filterStyle: 'outlined' | 'elevated' = 'outlined';
  @Input() variant: 'outlined' | 'ghost' = 'outlined';
  @Input() disabled = false;
  @Input() required = false;
  @Input() error = false;
  @Input() loading = false;
  @Input() multiple: boolean = false;
  @Input() clearable: boolean = false;
  @Input() hideSelectedOptions: boolean = false;
  @Input() maxPerRow?: number;

  @Input() documentTypeFilter: DocumentTypeFilter = 'all';

  @Input() set value(val: DocumentTypeSelectValue) {
    this._value = val;
  }
  get value(): DocumentTypeSelectValue {
    return this._value;
  }

  @Output() valueChange = new EventEmitter<DocumentTypeSelectValue>();

  options: SelectOption[] = [];
  readonly internalControl = new FormControl<DocumentTypeSelectValue>(null);

  get _buttonGroupOptions(): Array<{
    value: ContractorDocumentType;
    label: string;
  }> {
    return this.options.map((opt) => ({
      value: opt.value as ContractorDocumentType,
      label: opt.label
    }));
  }

  private filteredOptions: SelectOption[] = [];
  private _value: DocumentTypeSelectValue = null;
  private _internalSub?: Subscription;

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  private _onChange = (value: DocumentTypeSelectValue) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched = () => {};

  ngOnInit(): void {
    this.initializeOptions();
    this._internalSub = this.internalControl.valueChanges.subscribe((value) => {
      this._value = value;
      this._onChange(value);
      this._onTouched();
      this.valueChange.emit(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentTypeFilter']) {
      this.initializeOptions();
    }
  }

  ngOnDestroy(): void {
    this._internalSub?.unsubscribe();
  }

  private initializeOptions(): void {
    const allOptions = getGroupedDocumentTypeOptions();

    if (this.documentTypeFilter === 'all') {
      this.filteredOptions = allOptions;
    } else {
      const categoryLabel =
        DOCUMENT_LIST_CATEGORY_RECORD[this.documentTypeFilter];
      this.filteredOptions = allOptions.filter(
        (option) => option.group === categoryLabel
      );
    }

    this.options = [...this.filteredOptions];
  }

  writeValue(value: DocumentTypeSelectValue): void {
    this._value = value;
    this.internalControl.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: (value: DocumentTypeSelectValue) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSelectionChange(value: DocumentTypeSelectValue): void {
    this._value = value;
    this._onChange(value);
    this._onTouched();
    this.valueChange.emit(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onFocus(): void {}

  onSearch(value: string | null): void {
    if (!value) {
      this.options = [...this.filteredOptions];
    } else {
      const query = value.toLowerCase();
      this.options = this.filteredOptions.filter((option) =>
        option.label.toLowerCase().includes(query)
      );
    }
  }

  onBlur(): void {
    this._onTouched();
  }
}
